package ui.resource.detail

import androidx.compose.runtime.*
import api.ResourceType
import common.frontend.api.Io
import common.frontend.api.canResourceDelete
import common.frontend.api.canResourceEdit
import compose.assets.Svg
import compose.assets.svg.Information_slab_circle_outline
import compose.assets.svg.Palette_swatch_variant
import io.Repository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.err_connection_interrupted
import ssoft.frontend.generated.resources.media_detail__info__title
import ssoft.frontend.generated.resources.media_detail__usage__title
import tk.mallumo.compose.navigation.*
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.TabEntry
import tools.await
import tools.labelRes
import tools.printStackTraceInDebug
import ui.app.assign.detail.AppDetailArgs
import ui.app.rss.instance.detail.AppRssDetailInstanceArgs
import ui.campaign.detailv2.preview.CampaignPreviewArgs
import ui.company.detail.CompanyArgs
import ui.device.detail.DeviceArgs
import ui.location.detail.LocationArgs
import ui.server.detail.ServerArgs
import ui.snackBarStateGlobal
import ui.user.detail.UserArgs

@VM
class ResourceVM : NavigationViewModel() {

    private lateinit var scopeUi: ResourceScope

    enum class TabType {
        NONE,
        INFO,
        USAGE
    }

    var state by mutableDataState<ResourceScope.Wrapper>()
        private set

    var tabsScreenSmall by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    var tabsScreenLarge by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    private val _selectedTabSmall = MutableStateFlow(TabType.NONE)
    val selectedTabSmall: StateFlow<TabType> get() = _selectedTabSmall


    private val _selectedTabLarge = MutableStateFlow(TabType.NONE)
    val selectedTabLarge: StateFlow<TabType> get() = _selectedTabLarge

    sealed interface Action {
        data object Delete : Action
        data class DownloadPdf(val id: Long) : Action
        data class DownloadImage(val id: Long) : Action
        data class DownloadThumbnail(val id: Long) : Action
        data class DownloadAudio(val id: Long) : Action
        data class DownloadVideo(val id: Long) : Action
        data class DownloadAlphaVideo(val id: Long) : Action
        data class NavTo(val item: Io.Resource.Usage) : Action
        companion object {


            @Composable
            fun rememberAction(vm: ResourceVM): (Action) -> Unit {
                val nav = LocalNavigation.current
                return remember {
                    { act ->
                        when (act) {
                            Delete -> vm.delete()
                            is DownloadPdf -> vm.downloadPdf(act.id)
                            is DownloadImage -> vm.downloadImage(act.id)
                            is DownloadThumbnail -> vm.downloadThumbnail(act.id)
                            is DownloadVideo -> vm.downloadVideo(act.id)
                            is DownloadAudio -> vm.downloadAudio(act.id)
                            is DownloadAlphaVideo -> vm.downloadAlphaVideo(act.id)
                            is NavTo -> vm.navTo(nav, act.item)
                        }
                    }
                }
            }
        }
    }

    private fun downloadVideo(id: Long) {
        scopeUi.externals.openInBrowser(Repository.files.createUrlVideoH265(id))
    }

    private fun downloadAudio(id: Long) {
        scopeUi.externals.openInBrowser(Repository.files.createUrlAudio(id))
    }

    private fun downloadAlphaVideo(id: Long) {
        scopeUi.externals.openInBrowser(Repository.files.createUrlVideoAlpha(id))
    }

    private fun downloadThumbnail(id: Long) {
        scopeUi.externals.openInBrowser(Repository.files.createUrlThumbnail(id))
    }

    private fun downloadImage(id: Long) {
        scopeUi.externals.openInBrowser(Repository.files.createUrlImageOrigin(id))
    }

    private fun downloadPdf(id: Long) {
        scopeUi.externals.openInBrowser(Repository.files.createUrlDocument(id))
    }

    private fun navTo(nav: Navigation, item: Io.Resource.Usage) {
        with(scopeUi) {
            when {
                item.id.toString().endsWith("000") -> nav.navTo_ServerUI(ServerArgs(serverId = item.id))
                item.id.toString().endsWith("001") -> nav.navTo_CompanyUI(CompanyArgs(companyId = item.id))
                item.id.toString().endsWith("002") -> nav.navTo_LocationUI(
                    LocationArgs(
                        companyId = args.companyId,
                        locationId = item.id
                    )
                )

                item.id.toString().endsWith("006") -> nav.navTo_UserUI(UserArgs(userId = item.id))
                item.id.toString().endsWith("008") -> nav.navTo_DevicePreviewUI(DeviceArgs(deviceId = item.id))
                item.id.toString().endsWith("013") -> nav.navTo_CampaignPreviewUI(
                    CampaignPreviewArgs(
                        companyId = args.companyId,
                        campaignId = item.id
                    )
                )

                item.id.toString().endsWith("027") -> nav.navTo_CampaignPreviewUI(
                    CampaignPreviewArgs(
                        companyId = args.companyId,
                        campaignGroupId = item.id
                    )
                )

                item.id.toString().endsWith("017") -> {
                    nav.navTo_AppDetailUI(
                        AppDetailArgs(
                            companyId = args.companyId,
                            appId = item.id
                        )
                    )
                }

                item.id.toString().endsWith("018") -> nav.navTo_AppRssDetailInstanceUI(
                    AppRssDetailInstanceArgs(
                        id = item.id
                    )
                )

                else -> println("not defined for ${item.id}")
            }
        }
    }

    private fun delete() {
        scope.launch {
            runCatching {
                Repository.api.resource.delete(scopeUi.args.companyId, scopeUi.args.resourceId)
            }.onSuccess {
                scopeUi.nav.up()
            }.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    message = it.message!!,
                    withDismissAction = true
                )
            }
        }
    }


    fun init(scopeUi: ResourceScope) {
        this.scopeUi = scopeUi

        if (state !is DataState.Result) {
            state = DataState.Loading()
        }
        scope.launch {
            runCatching {
                val res = Repository.api.resource.single(scopeUi.args.companyId, scopeUi.args.resourceId)
                val title = ResourceType[res.type].labelRes.await() to "(${scopeUi.args.resourceId})"

                configureTabs()
                state = DataState.Result(
                    ResourceScope.Wrapper(
                        canDelete = scopeUi.login.canResourceDelete(scopeUi.args.companyId),
                        canEdit = scopeUi.login.canResourceEdit(scopeUi.args.companyId),
                        imageOrVideo = ResourceType.IMAGE match res.type || ResourceType.VIDEO match res.type,
                        title = title,
                        data = res
                    )
                )
            }.onFailure {
                it.printStackTrace()
                state = DataState.Error(Res.string.err_connection_interrupted.await())
            }
        }
    }

    private fun configureTabs() {
        tabsScreenSmall = buildTabs(true) {
            _selectedTabSmall.value = it.tag
        }
        tabsScreenLarge = buildTabs(false) {
            _selectedTabLarge.value = it.tag
        }
        _selectedTabSmall.value = tabsScreenSmall.first().tag
        _selectedTabLarge.value = tabsScreenLarge.firstOrNull()?.tag ?: TabType.NONE
    }

    private fun buildTabs(
        withMainScreen: Boolean,
        onClick: (TabEntry<TabType>) -> Unit
    ): List<TabEntry<TabType>> {
        return buildList {
            if (withMainScreen) {
                add(
                    TabEntry(
                        icon = Svg.Information_slab_circle_outline,
                        label = Res.string.media_detail__info__title,
                        onClick = onClick,
                        tag = TabType.INFO
                    )
                )
            }
            add(
                TabEntry(
                    icon = Svg.Palette_swatch_variant,
                    label = Res.string.media_detail__usage__title,
                    onClick = onClick,
                    tag = TabType.USAGE
                )
            )
        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }

}
